import i18n from '@/libs/i18n'
import store from '@/store'
import useBasicToast from '@/composable/useBasicToast'
import useEntityCrud from '@/composable/useEntityCrud'
import useErrorManager from '@/composable/useErrorManager'
import useModalDialog from '@/composable/useModalDialog'
import { ref } from '@vue/composition-api'

export default function useCommentList() {
  function convertResource(resource, selectedLang) {
    return {
      ...resource,
      sender: { fullName: resource.name, phoneNumber: resource.phone },
      lang: selectedLang,
    }
  }

  const ENDPOINT = 'suggestions'
  const MODULE_NAME = 'suggestions'
  const errorManager = useErrorManager()
  const modal = useModalDialog()
  const toast = useBasicToast()
  const exportExcelUrl = ref(null)

  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const {
    convertCollection, refTable, isSubmit, currentLanguage,
  } = entity
  // Table Handlers
  const tableColumns = [
    {
      key: 'sender', label: i18n.t('general.sender'), type: 'user', icon: 'UserIcon',
    },
    { key: 'email', label: i18n.t('general.email'), icon: 'AtSignIcon' },
    { key: 'archive', label: i18n.t('general.archive'), icon: 'ArchiveIcon' },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), sortable: true, type: 'dateTime',
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), sortable: true, type: 'dateTime',
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ]

  const toggleArchive = (id, archive = true) => {
    isSubmit.value = true
    const data = { archive }
    return store
      .dispatch('crud/update', { data: { id, data }, endpoint: ENDPOINT })
      .then(() => {
        isSubmit.value = false
        refTable.value.refetchData()
      })
      .catch(err => {
        isSubmit.value = false
        errorManager(err)
      })
  }

  const fetchArchive = params => store
    .dispatch('crud/fetchList', {
      queryParams: {
        lang: currentLanguage.value,
        ...params,
        archive: true,
      },
      endpoint: ENDPOINT,
    })
    .then(response => {
      const { data } = response
      return {
        data: convertCollection(data.data, currentLanguage.value),
        total: data.total,
      }
    })
    .catch(err => {
      errorManager(err)
    })

  const groupArchive = (ids, archive = true) => {
    const title = i18n.t('suggestions.archive')
    const message = archive ? i18n.t('suggestions.are_you_sure_group_archive') : i18n.t('suggestions.are_you_sure_group_unArchive')
    modal({
      title,
      message,
      type: archive ? 'info' : 'warning',
    }).then(res => {
      if (!res) return undefined
      isSubmit.value = true
      return store
        .dispatch('crud/groupArchive', {
          data: { ids },
          endpoint: ENDPOINT,
          archive,
        })
        .then(() => {
          toast({
            title: i18n.t('suggestions.group_archive_success'),
            type: 'success',
          })
        })
        .catch(err => {
          errorManager(err)
        })
        .finally(() => {
          refTable.value.refetchData()
          isSubmit.value = false
        })
    })
  }

  const fetchList = params => store
    .dispatch('crud/fetchList', {
      queryParams: {
        lang: currentLanguage.value,
        ...params,
        archive: false,
      },
      endpoint: ENDPOINT,
    })
    .then(response => {
      const { data } = response
      return {
        data: convertCollection(data.data, currentLanguage.value),
        total: data.total,
      }
    })
    .catch(err => {
      errorManager(err)
    })

  const exportExcel = () => {
    const queryParams = { responseType: 'blob' }
    return store
      .dispatch('crud/getExcel', {
        queryParams,
        endpoint: 'suggestions/excel',
      })
      .then(response => response)
      .catch(err => {
        errorManager(err)
      })
  }

  return {
    ...entity,
    convertResource,
    exportExcel,
    fetchArchive,
    fetchList,
    groupArchive,
    isSubmit,
    exportExcelUrl,
    tableColumns,
    toggleArchive,
  }
}
